import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeftIcon } from "@heroicons/react/24/outline";
import { PageLoader, TabNavigation, Button } from "../../ui";
import VaptSeverityBadge from "../components/VaptSeverityBadge";
import VaptStatusBadge from "../components/VaptStatusBadge";
import VaptCvssPanel from "../components/VaptCvssPanel";
import VaptEvidencePanel from "../components/VaptEvidencePanel";
import VaptTimeline from "../components/VaptTimeline";
import VaptRetestHistory from "../components/VaptRetestHistory";
import {
  getVaptFindingApi,
  updateFindingValidationApi,
  updateFindingRemediationApi,
  addFindingEvidenceApi,
  addFindingRetestApi,
  listVaptRemediationApi,
  listVaptRetestsApi,
} from "../../../services/axios/AuthApis/VAPTApis";
import { VAPT_VALIDATION_STATUSES, VAPT_REMEDIATION_STATUSES } from "../utils/vaptConstants";
import { getVaptErrorMessage } from "../utils/apiError";

const asArray = (data) => (Array.isArray(data) ? data : data?.results || []);

const VaptFindingDetail = () => {
  const { findingId } = useParams();
  const navigate = useNavigate();
  const [finding, setFinding] = useState(null);
  const [evidence, setEvidence] = useState([]);
  const [remediation, setRemediation] = useState(null);
  const [retests, setRetests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState("overview");
  const [saving, setSaving] = useState(false);
  const [retesting, setRetesting] = useState(false);
  // Action errors/results are shown as a banner; `error` (page-level) is
  // reserved for a finding that could not be loaded at all.
  const [notice, setNotice] = useState(null);

  const load = () => {
    setError(null);
    Promise.all([
      getVaptFindingApi(findingId),
      listVaptRemediationApi({ finding_id: findingId }),
      listVaptRetestsApi(findingId),
    ])
      .then(([data, remediationRes, retestsRes]) => {
        setFinding(data);
        const remList = asArray(remediationRes);
        setRemediation(remList[0] || null);
        setRetests(asArray(retestsRes));
        setEvidence(data?.evidence || []);
        setLoading(false);
      })
      .catch((err) => {
        setError(getVaptErrorMessage(err, "Failed to load finding."));
        setLoading(false);
      });
  };

  useEffect(() => {
    setLoading(true);
    load();
    // load is redefined each render but only needs to re-run when findingId changes
  }, [findingId]); // eslint-disable-line

  if (loading) return <PageLoader title="Loading finding…" />;
  if (error) {
    return (
      <div className="text-center py-16">
        <p className="text-gray-500">{error}</p>
      </div>
    );
  }
  if (!finding) {
    return (
      <div className="text-center py-16">
        <p className="text-gray-500">Finding not found.</p>
      </div>
    );
  }

  const tabs = [
    { id: "overview", name: "Overview" },
    { id: "asset", name: "Asset" },
    { id: "detection", name: "Detection" },
    { id: "evidence", name: "Evidence" },
    { id: "remediation", name: "Remediation" },
    { id: "timeline", name: "Timeline" },
    { id: "validation", name: "Validation & Retest" },
  ];

  const handleValidationChange = async (status) => {
    setSaving(true);
    try {
      await updateFindingValidationApi(finding.finding_id, status);
      load();
    } catch (err) {
      setNotice({ type: "error", text: getVaptErrorMessage(err, "Failed to update validation status.") });
    } finally {
      setSaving(false);
    }
  };

  const handleRemediationChange = async (status) => {
    setSaving(true);
    try {
      await updateFindingRemediationApi(finding.finding_id, status);
      load();
    } catch (err) {
      setNotice({ type: "error", text: getVaptErrorMessage(err, "Failed to update remediation status.") });
    } finally {
      setSaving(false);
    }
  };

  const handleAddEvidence = async (item) => {
    try {
      const created = await addFindingEvidenceApi(finding.finding_id, {
        type: item.type,
        reference: item.name,
      });
      setEvidence((prev) => [
        ...prev,
        {
          id: created.evidence_id,
          type: created.type,
          name: created.reference,
          addedAt: created.created_at,
          note: created.source_tool ? `Source tool: ${created.source_tool}` : item.note,
        },
      ]);
    } catch (err) {
      setNotice({ type: "error", text: getVaptErrorMessage(err, "Failed to add evidence.") });
    }
  };

  // Triggers the REAL re-verification: with no `result` in the payload the
  // backend re-runs the finding's tool against the same target and decides
  // Resolved / Still Vulnerable itself (or honestly "Unable to Verify").
  // The UI must never send a hard-coded pass.
  const handleAddRetest = async () => {
    setRetesting(true);
    setNotice(null);
    try {
      const retest = await addFindingRetestApi(finding.finding_id, {});
      setNotice({
        type: "info",
        text: `Retest #${retest?.retest_number ?? ""} finished: ${retest?.result || "no result"} (${retest?.execution_mode || "unknown"} mode). ${retest?.notes || ""}`.trim(),
      });
      load();
    } catch (err) {
      setNotice({ type: "error", text: getVaptErrorMessage(err, "Failed to run the retest.") });
    } finally {
      setRetesting(false);
    }
  };

  // Adapt real Retest rows to the shape VaptRetestHistory expects, without
  // modifying that shared component.
  const retestHistoryFinding = {
    title: finding.title,
    tool: (finding.detected_by_tools || []).join(", ") || "—",
    detectedAt: finding.created_at || null,
    validationStatus: finding.validation_status,
    retests: retests
      .slice()
      .sort((a, b) => (a.retest_number ?? 0) - (b.retest_number ?? 0))
      .map((r) => ({
        id: r.retest_id ?? `${r.retest_number}-${r.performed_at}`,
        attempt: r.retest_number,
        date: r.performed_at,
        tester: r.performed_by,
        notes: r.notes,
        result: r.result,
      })),
  };

  return (
    <div className="space-y-6">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
        <ArrowLeftIcon className="w-4 h-4" /> Back
      </button>

      {notice && (
        <div
          role="alert"
          className={`text-sm rounded-lg px-4 py-2.5 border ${
            notice.type === "error" ? "bg-red-50 border-red-200 text-red-700" : "bg-blue-50 border-blue-200 text-blue-800"
          }`}
        >
          {notice.text}
        </div>
      )}

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-3 mb-1 flex-wrap">
            <h2 className="text-lg font-bold text-gray-900">{finding.title}</h2>
            <VaptSeverityBadge severity={finding.severity} />
            <VaptStatusBadge status={finding.validation_status} />
            <VaptStatusBadge status={finding.finding_status} />
          </div>
          <p className="text-sm text-gray-500">
            {finding.project_name || finding.project_display_id || "Asset VAPT Project"} • Target: {finding.target_display ?? "—"}
          </p>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-2.5">{error}</div>
      )}

      <TabNavigation tabs={tabs} activeTab={tab} onTabChange={setTab} boxedActive />

      {tab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl p-5 space-y-3">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs text-gray-400 uppercase">CWE</p>
                <p className="text-gray-800">{finding.cwe_id || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase">CVE</p>
                <p className="text-gray-800">{finding.cve_id || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase">Remediation Priority</p>
                <p className="text-gray-800">{finding.remediation_priority ?? "—"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase mb-1">Detected By ({(finding.detected_by_tools || []).length} tool(s))</p>
                {(finding.detected_by_tools || []).length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                    {finding.detected_by_tools.map((t) => (
                      <span key={t} className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                        {t}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-800">—</p>
                )}
              </div>
            </div>
            {finding.referenceMapping?.business_risk_context && (
              <div className="border-t border-gray-100 pt-3 mt-3">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Business Risk Context (separate from CVSS technical severity)</p>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-xs text-gray-400 uppercase">Internet Exposed</p>
                    <p className="text-gray-800">{finding.referenceMapping.business_risk_context.internet_exposed}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase">Asset Criticality</p>
                    <p className="text-gray-800">{finding.referenceMapping.business_risk_context.asset_criticality}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase">Sensitive Data</p>
                    <p className="text-gray-800">{finding.referenceMapping.business_risk_context.sensitive_data}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 uppercase">Business Risk</p>
                    <p className="text-gray-800 font-semibold">{finding.referenceMapping.business_risk_context.business_risk}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
          <VaptCvssPanel
            cvssV3={{ score: finding.cvss_score, vector: finding.cvss_vector }}
            cvssV4={null}
          />
        </div>
      )}

      {tab === "asset" && (
        <div className="bg-white border border-gray-200 rounded-xl p-5 grid grid-cols-2 gap-4 text-sm">
          <div>
            <p className="text-xs text-gray-400 uppercase">Target</p>
            <p className="text-gray-800">{finding.target_display ?? "—"}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 uppercase">Project</p>
            <p className="text-gray-800">{finding.project_name || finding.project_display_id || "Asset VAPT Project"}</p>
          </div>
          <div>
            <p className="text-xs text-gray-400 uppercase">Scan</p>
            <p className="text-gray-800">{finding.scan_display_id || "—"}</p>
          </div>
        </div>
      )}

      {tab === "detection" && (
        <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs text-gray-400 uppercase">Detected By Tools</p>
              <p className="text-gray-800">{(finding.detected_by_tools || []).join(", ") || "—"}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400 uppercase">Detected At</p>
              <p className="text-gray-800">{finding.created_at ? new Date(finding.created_at).toLocaleString() : "—"}</p>
            </div>
            {finding.cve_id && (
              <div>
                <p className="text-xs text-gray-400 uppercase">CVE</p>
                <p className="text-gray-800">{finding.cve_id}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {tab === "evidence" && (
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <VaptEvidencePanel evidence={evidence} onAdd={handleAddEvidence} />
        </div>
      )}

      {tab === "remediation" && (
        <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Remediation Status</p>
            <div className="flex flex-wrap gap-2">
              {VAPT_REMEDIATION_STATUSES.map((s) => (
                <button
                  key={s}
                  disabled={saving}
                  onClick={() => handleRemediationChange(s)}
                  className={`text-xs px-3 py-1.5 rounded-full border ${
                    finding.finding_status === s
                      ? "bg-primary-600 border-primary-600 text-white"
                      : "border-gray-200 text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
          {remediation ? (
            <div className="grid grid-cols-2 gap-4 text-sm border-t border-gray-100 pt-4">
              <div>
                <p className="text-xs text-gray-400 uppercase">Assigned To</p>
                <p className="text-gray-800">{remediation.assigned_to || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase">Owner</p>
                <p className="text-gray-800">{remediation.owner || "—"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase">SLA (days)</p>
                <p className="text-gray-800">{remediation.sla_days ?? "—"}</p>
              </div>
              <div>
                <p className="text-xs text-gray-400 uppercase">Due Date</p>
                <p className="text-gray-800">{remediation.due_date || "—"}</p>
              </div>
              <div className="col-span-2">
                <p className="text-xs text-gray-400 uppercase">Recommendation</p>
                <p className="text-gray-800">{remediation.recommendation || "—"}</p>
              </div>
              {remediation.comments && (
                <div className="col-span-2">
                  <p className="text-xs text-gray-400 uppercase">Comments</p>
                  <p className="text-gray-800">{remediation.comments}</p>
                </div>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-400 border-t border-gray-100 pt-4">
              No remediation record has been created for this finding yet.
            </p>
          )}
        </div>
      )}

      {tab === "timeline" && (
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          {/*
            The finding model does not store a validation/remediation event
            timeline — only current status fields. Do not fabricate stage
            dates; show the stepper based on current status only.
          */}
          <p className="text-xs text-gray-400 mb-3">
            Detailed stage-by-stage history is not currently available — this view reflects current status only.
          </p>
          <VaptTimeline timeline={[]} />
        </div>
      )}

      {tab === "validation" && (
        <div className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Validation Status</p>
            <div className="flex flex-wrap gap-2 mb-4">
              {VAPT_VALIDATION_STATUSES.map((s) => (
                <button
                  key={s}
                  disabled={saving}
                  onClick={() => handleValidationChange(s)}
                  className={`text-xs px-3 py-1.5 rounded-full border ${
                    finding.validation_status === s
                      ? "bg-primary-600 border-primary-600 text-white"
                      : "border-gray-200 text-gray-600 hover:bg-gray-50"
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
            <Button size="sm" onClick={handleAddRetest} disabled={retesting}>
              {retesting ? "Running retest…" : "Run Retest"}
            </Button>
            <p className="text-xs text-gray-400 mt-2">
              Re-runs the original tool against the same target and records the actual outcome.
            </p>
          </div>
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <VaptRetestHistory finding={retestHistoryFinding} />
          </div>
        </div>
      )}
    </div>
  );
};

export default VaptFindingDetail;
